import { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router';
import type { QuestState } from '@tg/shared';
import { paths } from '@/app/paths';
import { useLocale } from '@/i18n/useLocale';
import { useSocketEvent } from '@/socket/socket';
import { useSession } from '@/stores/session';
import { toast, useToasts } from '@/stores/toasts';
import { wallet } from '@/wallet/wallet';
import { untilUtcMidnight } from '@/features/social/BadgesListener';
import { useQuest } from './store';

/**
 * The daily quest of the signed-in player: loaded on sign-in and again after the UTC midnight (a new quest), kept live
 * by `quest:progress`. When the last round counts, one quiet toast says the reward is waiting (a link to the lobby
 * from a game screen) — no sound on a game screen, no celebration: the player claims it when they like.
 */
export function QuestListener() {
  const status = useSession((st) => st.status);
  const day = useQuest((st) => st.quest?.day ?? null);
  const l = useLocale();
  const { pathname } = useLocation();

  useEffect(() => {
    if (status !== 'user') {
      useQuest.getState().reset();
      return;
    }
    const ac = new AbortController();
    void useQuest.getState().load({ signal: ac.signal });
    return () => ac.abort();
  }, [status]);

  useEffect(() => {
    if (status !== 'user' || !day) return;
    const t = setTimeout(() => void useQuest.getState().load(), untilUtcMidnight());
    return () => clearTimeout(t);
  }, [status, day]);

  useSocketEvent<[QuestState]>('quest:progress', (q) => {
    if (useSession.getState().status !== 'user') return;
    const prev = useQuest.getState().set(q);
    if (q.status !== 'complete' || prev?.status !== 'active' || prev.day !== q.day) return;
    const values = { amount: q.reward };
    if (pathname === paths.lobby(l)) {
      useToasts.getState().push({ kind: 'success', titleId: 'quests.toast.complete', textId: 'quests.toast.completeText', values });
    } else {
      toast.link('quests.toast.complete', { to: paths.lobby(l), labelId: 'quests.toast.open' }, values, 'quests.toast.completeText');
    }
  });

  return null;
}

/** «Claim»: the reward lands in the wallet (coins fly to the balance), a short toast; errors as toasts. */
export function useQuestClaim(): { claim(): Promise<void>; claiming: boolean } {
  const claiming = useQuest((st) => st.claiming);
  const claim = useCallback(async () => {
    try {
      const r = await useQuest.getState().claim();
      await wallet.receive({ balance: r.balance, delta: r.amount, reason: 'quest_reward' });
      toast.success('quests.toast.claimed', { amount: r.amount });
    } catch (e) {
      if (e instanceof Error && e.message === 'busy') return;
      toast.error(e);
      // the state may have moved on (claimed in another tab, a new day)
      void useQuest.getState().load();
    }
  }, []);
  return { claim, claiming };
}
